
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../App';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft, ShieldCheck, Sparkles, TicketPercent, CheckCircle2 } from 'lucide-react';
import { ref, get, onValue } from 'firebase/database';
import { db } from '../firebase';

const CartPage: React.FC = () => {
  const { cart, removeFromCart, updateCartQuantity } = useApp();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState('');
  const [couponError, setCouponError] = useState('');
  const [shippingConfig, setShippingConfig] = useState({ threshold: 250, fee: 45 });

  useEffect(() => {
    onValue(ref(db, 'settings/shipping'), (snapshot) => {
      if (snapshot.exists()) setShippingConfig(snapshot.val());
    });
  }, []);

  const subtotal = cart.reduce((acc, item) => acc + (item.product.price * item.quantity), 0);
  
  const handleApplyCoupon = async () => {
    setCouponError('');
    if (!couponCode) return;

    try {
      const couponsRef = ref(db, 'coupons');
      const snapshot = await get(couponsRef);
      if (snapshot.exists()) {
        const coupons = snapshot.val();
        const coupon = Object.values(coupons).find((c: any) => c.code === couponCode.toUpperCase() && c.isActive) as any;

        if (coupon) {
          if (coupon.expiryDate && coupon.expiryDate < Date.now()) {
            setCouponError('Bu kuponun süresi dolmuş.');
            return;
          }
          if (coupon.isSingleUse && coupon.isUsed) {
            setCouponError('Bu kupon daha önce kullanılmış.');
            return;
          }

          let discountAmount = 0;
          if (coupon.type === 'percentage') {
            discountAmount = (subtotal * coupon.value) / 100;
          } else {
            discountAmount = coupon.value;
          }

          setDiscount(discountAmount);
          setAppliedCoupon(coupon.code);
          setCouponError('');
        } else {
          setCouponError('Geçersiz kupon kodu.');
        }
      } else {
        setCouponError('Kupon bulunamadı.');
      }
    } catch (error) {
      setCouponError('Kupon kontrol edilirken bir hata oluştu.');
    }
  };

  const discountedSubtotal = Math.max(0, subtotal - discount);
  const shippingFee = discountedSubtotal >= shippingConfig.threshold ? 0 : shippingConfig.fee;
  const finalTotal = discountedSubtotal + shippingFee;

  if (cart.length === 0) {
    return (
      <div className="container mx-auto px-4 py-32 text-center">
        <div className="w-24 h-24 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-8 text-stone-300">
          <ShoppingBag size={48} />
        </div>
        <h2 className="text-3xl font-serif font-bold text-stone-900 mb-4">Sepetiniz Boş</h2>
        <p className="text-stone-500 mb-10 max-w-sm mx-auto font-medium leading-relaxed">Sepetinizde henüz bir ürün bulunmuyor. Evinizi güzelleştirecek yeni parçalar keşfedin.</p>
        <Link to="/" className="inline-flex items-center space-x-3 bg-amber-800 text-white px-10 py-4 rounded-2xl font-bold shadow-xl shadow-amber-900/20 hover:bg-amber-900 transition-all">
          <ArrowLeft size={20} />
          <span>Koleksiyonu İncele</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 md:px-6 pb-20">
      <h1 className="text-4xl font-serif font-bold text-stone-900 mb-12 flex items-center">
        Alışveriş Sepetim <span className="ml-6 text-xs font-black text-stone-400 bg-stone-100 px-4 py-1.5 rounded-full uppercase tracking-widest">{cart.length} Ürün</span>
      </h1>

      <div className="grid lg:grid-cols-3 gap-12">
        {/* Cart List */}
        <div className="lg:col-span-2 space-y-6">
          {cart.map((item) => (
            <div key={item.product.id} className="bg-white p-8 rounded-[2.5rem] border border-stone-100 flex flex-col md:flex-row items-center gap-8 group shadow-sm hover:shadow-md transition-all">
              <Link to={`/product/${item.product.id}`} className="w-32 h-32 bg-stone-50 rounded-2xl flex-shrink-0 p-3 overflow-hidden">
                <img 
                  src={(item.product.images && item.product.images.length > 0) ? item.product.images[0] : 'https://images.unsplash.com/photo-1603006905003-be475563bc59?auto=format&fit=crop&q=80&w=300'} 
                  className="w-full h-full object-cover rounded-xl group-hover:scale-110 transition-transform duration-700" 
                  alt={item.product.name}
                />
              </Link>
              
              <div className="flex-1 text-center md:text-left">
                <div className="text-[10px] font-black text-amber-700 uppercase tracking-[0.2em] mb-2">{item.product.brand || "Lumina Craft"}</div>
                <Link to={`/product/${item.product.id}`} className="text-xl font-serif font-bold text-stone-900 hover:text-amber-800 transition-colors block mb-3 line-clamp-1">{item.product.name}</Link>
                <div className="flex items-center justify-center md:justify-start space-x-4">
                  <span className="text-stone-900 font-black text-2xl">₺{item.product.price.toLocaleString()}</span>
                  {item.product.oldPrice && <span className="text-stone-400 text-sm line-through">₺{item.product.oldPrice.toLocaleString()}</span>}
                </div>
              </div>

              <div className="flex flex-col items-center gap-6">
                <div className="flex items-center bg-stone-50 rounded-2xl p-1.5 border border-stone-100">
                  <button 
                    onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)}
                    className="w-10 h-10 flex items-center justify-center text-stone-400 hover:text-amber-800 transition-colors"
                  ><Minus size={18} /></button>
                  <span className="w-10 text-center font-bold text-stone-900">{item.quantity}</span>
                  <button 
                    onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)}
                    className="w-10 h-10 flex items-center justify-center text-stone-400 hover:text-amber-800 transition-colors"
                  ><Plus size={18} /></button>
                </div>
                <button 
                  onClick={() => removeFromCart(item.product.id)}
                  className="flex items-center space-x-2 text-[10px] font-black text-red-400 hover:text-red-500 uppercase tracking-widest"
                >
                  <Trash2 size={16} />
                  <span>Kaldır</span>
                </button>
              </div>
            </div>
          ))}

          {/* Coupon Input */}
          <div className="bg-white p-8 rounded-[2.5rem] border border-stone-100 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
            <div className="flex items-center space-x-4">
               <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-700">
                  <TicketPercent size={24} />
               </div>
               <div>
                  <p className="font-bold text-stone-900">İndirim Kuponu</p>
                  <p className="text-xs text-stone-400">Bir kuponunuz varsa buraya girin.</p>
               </div>
            </div>
            <div className="flex w-full md:w-auto gap-3">
               <input 
                type="text" 
                placeholder="KUPON KODU" 
                value={couponCode}
                onChange={e => setCouponCode(e.target.value.toUpperCase())}
                className="flex-1 md:w-48 bg-stone-50 border border-stone-100 rounded-xl px-4 py-3 text-sm font-bold uppercase outline-none focus:ring-2 focus:ring-amber-500"
               />
               <button 
                onClick={handleApplyCoupon}
                className="bg-stone-900 text-white px-6 py-3 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-amber-800 transition-all"
               >
                Uygula
               </button>
            </div>
          </div>
          {couponError && <p className="text-red-500 text-xs font-bold px-8">{couponError}</p>}
          {appliedCoupon && (
            <div className="flex items-center space-x-2 px-8 text-emerald-600 font-bold text-xs">
              <CheckCircle2 size={14} />
              <span>"{appliedCoupon}" kuponu başarıyla uygulandı!</span>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="space-y-6">
          <div className="bg-stone-900 text-white p-10 rounded-[3rem] shadow-2xl relative overflow-hidden">
            <h3 className="text-2xl font-serif font-bold mb-10">Ödeme Özeti</h3>
            
            <div className="space-y-5 mb-10">
              <div className="flex justify-between text-stone-400 font-medium">
                <span>Ara Toplam</span>
                <span>₺{subtotal.toLocaleString()}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-emerald-400 font-bold">
                  <span>İndirim ({appliedCoupon})</span>
                  <span>-₺{discount.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-stone-400 font-medium">
                <span>Kargo</span>
                {shippingFee === 0 ? (
                  <span className="text-amber-500 font-bold uppercase tracking-widest text-xs">Ücretsiz</span>
                ) : (
                  <span className="text-white font-bold">₺{shippingFee.toLocaleString()}</span>
                )}
              </div>
              {shippingFee > 0 && (
                 <p className="text-[10px] text-amber-500 font-bold uppercase tracking-widest text-right -mt-4">
                    ₺{(shippingConfig.threshold - discountedSubtotal).toLocaleString()} TL Daha Ekle Kargo Bedava!
                 </p>
              )}
              <div className="h-px bg-stone-800 my-6"></div>
              <div className="flex justify-between items-end">
                <div>
                  <span className="block text-stone-400 text-xs font-bold uppercase tracking-widest mb-2">Genel Toplam</span>
                  <span className="text-4xl font-black text-white">₺{finalTotal.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <button 
              onClick={() => navigate('/checkout', { state: { discount, couponCode: appliedCoupon } })}
              className="w-full bg-amber-700 text-white py-5 rounded-2xl font-bold text-lg hover:bg-amber-600 transition-all shadow-xl shadow-amber-900/20 active:scale-95 flex items-center justify-center space-x-3"
            >
              <Sparkles size={20} />
              <span>Siparişi Tamamla</span>
            </button>
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-600/5 rounded-full -mr-16 -mt-16 blur-2xl"></div>
          </div>

          <div className="bg-white p-8 rounded-[2.5rem] border border-stone-100 flex items-center space-x-5">
            <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-700 shadow-inner">
              <ShieldCheck size={24} />
            </div>
            <div>
              <p className="text-sm font-bold text-stone-900">Güvenli Ödeme</p>
              <p className="text-xs text-stone-400 font-medium">Tüm işlemler 256-bit SSL ile korunmaktadır.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
